import { CheckOutlined, WarningOutlined } from '@ant-design/icons';
import { Button, Space, Spin, Tabs, Typography } from 'antd';
import _ from 'lodash';
import React, { ReactElement, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import IApplication from '~Api/Application/IApplication';
import IApplicationProperty from '~Api/Application/IApplicationProperty';
import IBorrower from '~Api/Application/IBorrower';
import ICondition from '~Api/Application/ICondition';
import IConditionDocument from '~Api/Application/IConditionDocument';
import IRfiItem from '~Api/Application/IRfiItem';
import {
    applicationBorrowersListAction,
    applicationConditionDocumentsListAction,
    applicationConditionsListAction,
    applicationGetAction,
    applicationRfiItemsListAction,
} from '~Applications/actions';
import {
    applicationBorrowersSelector,
    applicationConditionDocumentsByConditionUuidSelector,
    applicationEntityConditionsSelector,
    applicationPropertiesSelector,
    applicationRfiItemsSelector,
    applicationSelector,
} from '~Applications/selectors';
import ConditionDocumentsList from '~Applications/Application/ConditionDocumentsList';
import { IGlobalState } from '~reducer';
import { IDictionary } from '~utilities/IDictionary';
import Layout from './Layout';
import {
    approvedConditionDocumentExists,
} from '~Applications/utilities';
import WorkflowStatusEnum from '~Api/Application/WorkflowStatusEnum';
import { aggregatorsSelector } from '~Aggregators/selectors';
import { aggregatorsListAction } from '~Aggregators/actions';
import IAggregator from '~Api/Aggregator/IAggregator';
import ConditionAddModal from './ConditionAddModal';
import ModalStateEnum from '~UI/ModalStateEnum';
import HashTabs from '~UI/HashTabs';

const nextWorkflowStatuses: IDictionary<WorkflowStatusEnum> = {
    [WorkflowStatusEnum.Draft]: WorkflowStatusEnum.New,
    [WorkflowStatusEnum.New]: WorkflowStatusEnum.ConditionalApproval,
    [WorkflowStatusEnum.ConditionalApproval]: WorkflowStatusEnum.Underwriting,
    [WorkflowStatusEnum.Underwriting]: WorkflowStatusEnum.FormalApproval,
    [WorkflowStatusEnum.FormalApproval]: WorkflowStatusEnum.LegalDocuments,
    [WorkflowStatusEnum.LegalDocuments]: WorkflowStatusEnum.Settlement,
    [WorkflowStatusEnum.Settlement]: WorkflowStatusEnum.Warehoused,
};

export default function Underwriting(): ReactElement {
    const { applicationUuid } = useParams<{ applicationUuid: string }>();
    const [ isAddConditionModalOpen, setIsAddConditionModalOpen ] = useState<ModalStateEnum>(ModalStateEnum.Off);
    const [ openModalEntityUuid, setOpenModalEntityUuid ] = useState<string>(null);

    const aggregators: IDictionary<IAggregator> = useSelector(aggregatorsSelector);
    const application: IApplication = useSelector((state: IGlobalState): IApplication => applicationSelector(state, applicationUuid));
    const applicationEntityConditions: IDictionary<IDictionary<ICondition>> = useSelector((state: IGlobalState): IDictionary<IDictionary<ICondition>> => applicationEntityConditionsSelector(state, applicationUuid));
    const applicationProperties: IDictionary<IApplicationProperty> = useSelector((state: IGlobalState): IDictionary<IApplicationProperty> => applicationPropertiesSelector(state, applicationUuid));
    const borrowers: IDictionary<IBorrower> = useSelector((state: IGlobalState): IDictionary<IBorrower> => applicationBorrowersSelector(state, applicationUuid));
    const conditionDocumentsByConditionUuid: IDictionary<IConditionDocument[]> = useSelector((state: IGlobalState): IDictionary<IConditionDocument[]> => applicationConditionDocumentsByConditionUuidSelector(state, applicationUuid));
    const rfiItems: IDictionary<IRfiItem> = useSelector((state: IGlobalState): IDictionary<IRfiItem> => applicationRfiItemsSelector(state, applicationUuid));

    const dispatch: Dispatch = useDispatch();

    useEffect((): void => {
        if (!aggregators) {
            dispatch(aggregatorsListAction());
        }
    }, [
        aggregators,
        dispatch,
    ]);

    useEffect((): void => {
        if (!application) {
            dispatch(applicationGetAction(applicationUuid));
        }
    }, [
        application,
        applicationUuid,
        dispatch,
    ]);

    useEffect((): void => {
        if (!borrowers) {
            dispatch(applicationBorrowersListAction(applicationUuid));
        }
    }, [
        applicationUuid,
        borrowers,
        dispatch,
    ]);

    useEffect((): void => {
        if (!conditionDocumentsByConditionUuid) {
            dispatch(applicationConditionDocumentsListAction(applicationUuid));
        }
    }, [
        applicationUuid,
        conditionDocumentsByConditionUuid,
        dispatch,
    ]);

    useEffect((): void => {
        if (!applicationEntityConditions) {
            dispatch(applicationConditionsListAction(applicationUuid));
        }
    }, [
        applicationEntityConditions,
        applicationUuid,
        dispatch,
    ]);

    useEffect((): void => {
        if (!rfiItems) {
            dispatch(applicationRfiItemsListAction(applicationUuid));
        }
    }, [
        applicationUuid,
        dispatch,
        rfiItems,
    ]);

    useEffect((): () => void => {
        const refreshInterval: NodeJS.Timeout = setInterval((): void => {
            dispatch(applicationConditionDocumentsListAction(applicationUuid));
            dispatch(applicationConditionsListAction(applicationUuid));
            dispatch(applicationRfiItemsListAction(applicationUuid));
        }, 5 * 60 * 1000);

        return (): void => clearInterval(refreshInterval);
    }, [
        applicationUuid,
        dispatch,
    ]);

    const onClickAddConditionModal: (openModalEntityUuid: string) => void = (openModalEntityUuid: string): void => {
        setIsAddConditionModalOpen(ModalStateEnum.Open);
        setOpenModalEntityUuid(openModalEntityUuid);
    };

    const onCancelAddConditionModal: () => void = (): void => {
        setIsAddConditionModalOpen(ModalStateEnum.Closed);
        setOpenModalEntityUuid(null);
    };

    const entityConditionDocumentsComplete: (entityUuid: string) => boolean = (entityUuid: string): boolean => {
        if (_.some(applicationEntityConditions[entityUuid] || [], (condition: ICondition): boolean => (
            condition.requiredWorkflowStatuses.includes(nextWorkflowStatuses[application.workflowStatus]) &&
            !approvedConditionDocumentExists(conditionDocumentsByConditionUuid[condition.uuid] || [])
        ))) {
            return false;
        }

        return true;
    };

    if (
        !aggregators ||
        !application ||
        !applicationEntityConditions ||
        !applicationProperties ||
        !borrowers ||
        !conditionDocumentsByConditionUuid ||
        !rfiItems
    ) {
        return (
            <Layout applicationUuid={applicationUuid} section='underwriting'>
                <Typography.Title level={2}>Underwriting Checklist</Typography.Title>
                <Spin />
            </Layout>
        );
    }

    const borrowerTabsBlock: JSX.Element[] = _.map(borrowers, (borrower: IBorrower): JSX.Element => {
        const onClickAddApplicationBorrowerCondition: () => void = (): void => onClickAddConditionModal(borrower.uuid);
        const borrowerConditionModal: ReactElement = openModalEntityUuid === borrower.uuid && isAddConditionModalOpen !== ModalStateEnum.Off && (
            <ConditionAddModal
                application={application}
                applicationBorrower={borrower}
                isOpen={isAddConditionModalOpen === ModalStateEnum.Open}
                onCancel={onCancelAddConditionModal}
            />
        );

        return (
            <Tabs.TabPane
                tab={<Typography>{entityConditionDocumentsComplete(borrower.uuid) ? <CheckOutlined /> : <WarningOutlined />} {borrower.dealBorrower.formattedName}</Typography>}
                key={`borrower-${borrower.uuid}`}
            >
                <ConditionDocumentsList
                    key={`condition-document-borrower-${borrower.uuid}`}
                    application={application}
                    applicationBorrower={borrower}
                />
                <Button className='add-condition' onClick={onClickAddApplicationBorrowerCondition}>Add Condition</Button>
                {borrowerConditionModal}
            </Tabs.TabPane>
        );
    });

    const propertyTabsBlock: JSX.Element[] = _.reduce(applicationProperties, (tabs: JSX.Element[], applicationProperty: IApplicationProperty): JSX.Element[] => {
        if (applicationProperty.dealProperty.dischargeTime) {
            return tabs;
        }

        const onClickAddApplicationPropertyCondition: () => void = (): void => onClickAddConditionModal(applicationProperty.uuid);
        const propertyConditionModal: ReactElement = openModalEntityUuid === applicationProperty.uuid && isAddConditionModalOpen !== ModalStateEnum.Off && (
            <ConditionAddModal
                application={application}
                applicationProperty={applicationProperty}
                isOpen={isAddConditionModalOpen === ModalStateEnum.Open}
                onCancel={onCancelAddConditionModal}
            />
        );

        tabs.push(
            <Tabs.TabPane
                tab={<Typography>{entityConditionDocumentsComplete(applicationProperty.uuid) ? <CheckOutlined /> : <WarningOutlined />} {applicationProperty.dealProperty.streetAddress}</Typography>}
                key={`property-${applicationProperty.uuid}`}
            >
                <ConditionDocumentsList
                    key={`condition-document-property${applicationProperty.uuid}`}
                    application={application}
                    applicationProperty={applicationProperty}
                />
                <Button className='add-condition' onClick={onClickAddApplicationPropertyCondition}>Add Condition</Button>
                {propertyConditionModal}
            </Tabs.TabPane>,
        );

        return tabs;
    }, []);

    const solictorDownloadsButton: JSX.Element = !application.isExtension && (
        <Link to={`/applications/${application.uuid}/solicitor-downloads`}>
            <Button>Download Documents</Button>
        </Link>
    );
    const onClickAddApplicationCondition: () => void = (): void => onClickAddConditionModal(application.uuid);
    const applicationConditionModal: ReactElement = openModalEntityUuid === application.uuid && isAddConditionModalOpen !== ModalStateEnum.Off && (
        <ConditionAddModal
            application={application}
            isOpen={isAddConditionModalOpen === ModalStateEnum.Open}
            onCancel={onCancelAddConditionModal}
        />
    );

    return (
        <Layout applicationUuid={applicationUuid} section='underwriting'>
            <Space className='actions'>
                {solictorDownloadsButton}
            </Space>
            <Typography.Title level={2}>Underwriting Checklist</Typography.Title>
            <HashTabs defaultActiveKey={`application-${application.uuid}`} type='card'>
                <Tabs.TabPane
                    tab={<Typography>{entityConditionDocumentsComplete(application.uuid) ? <CheckOutlined /> : <WarningOutlined />} Application</Typography>}
                    key={`application-${application.uuid}`}
                >
                    <ConditionDocumentsList
                        key={`condition-document-application-${application.uuid}`}
                        application={application}
                    />
                    <Button className='add-condition' onClick={onClickAddApplicationCondition}>Add Condition</Button>
                    {applicationConditionModal}
                </Tabs.TabPane>
                {borrowerTabsBlock}
                {propertyTabsBlock}
            </HashTabs>
        </Layout>
    );
}
